import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MapPin, Package, Loader2, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { LoadingState, EmptyState } from "@/components/States";
import { UNITS, CITIES } from "@/lib/constants";
import { formatTRY, timeAgo } from "@/lib/format";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function BuyRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/buy-requests").then(({ data }) => setItems(data.items)).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/categories").then(({ data }) => setCategories(data.items)); }, []);

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Alım Talepleri</h1>
          <p className="mt-1 text-slate-500">Alıcıların aradığı ürünler — satıcıysanız teklif verin</p>
        </div>
        <CreateBuyRequest categories={categories} user={user} navigate={navigate} onDone={load} />
      </div>

      {loading ? <LoadingState /> : items.length === 0 ? (
        <div className="mt-8"><EmptyState title="Henüz alım talebi yok" icon={ClipboardList} /></div>
      ) : (
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {items.map((b) => (
            <Link key={b.buy_request_id} to={`/alim-talepleri/${b.buy_request_id}`} data-testid={`buyreq-${b.buy_request_id}`}
              className="rounded-xl border border-slate-200 bg-white p-5 transition-all hover:shadow-md hover:-translate-y-0.5">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-lg font-bold text-slate-900">{b.product}</h3>
                {b.target_price != null && <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-sm font-semibold text-emerald-700">~{formatTRY(b.target_price)}/{b.unit}</span>}
              </div>
              <p className="mt-2 line-clamp-2 text-sm text-slate-600">{b.description}</p>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                {b.quantity != null && <span className="flex items-center gap-1"><Package size={14} />{b.quantity} {b.unit}</span>}
                {b.city && <span className="flex items-center gap-1"><MapPin size={14} />{b.city}</span>}
                <span className="ml-auto text-xs text-slate-400">{timeAgo(b.created_at)}</span>
              </div>
              <p className="mt-2 text-xs text-slate-400">Talep eden: {b.buyer?.name}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateBuyRequest({ categories, user, navigate, onDone }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ product: "", category: "", quantity: "", unit: "ton", target_price: "", city: "", description: "" });
  const set = (k) => (v) => setF({ ...f, [k]: v });

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/buy-requests", {
        product: f.product, category: f.category || null, quantity: f.quantity ? parseFloat(f.quantity) : null,
        unit: f.unit, target_price: f.target_price ? parseFloat(f.target_price) : null, city: f.city || null, description: f.description,
      });
      toast.success("Alım talebiniz oluşturuldu");
      setOpen(false);
      setF({ product: "", category: "", quantity: "", unit: "ton", target_price: "", city: "", description: "" });
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!user) { navigate("/giris"); return; } setOpen(o); }}>
      <DialogTrigger asChild>
        <Button className="gap-1.5 bg-emerald-600 font-semibold hover:bg-emerald-700" data-testid="create-buyrequest-btn"><Plus size={18} /> Alım Talebi Oluştur</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Alım Talebi Oluştur</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Ürün *</Label><Input value={f.product} onChange={(e) => set("product")(e.target.value)} data-testid="br-product" placeholder="Örn: Domates" /></div>
          <div className="space-y-1.5">
            <Label>Kategori</Label>
            <Select value={f.category} onValueChange={set("category")}><SelectTrigger data-testid="br-category"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Miktar</Label><Input type="number" value={f.quantity} onChange={(e) => set("quantity")(e.target.value)} data-testid="br-quantity" /></div>
            <div className="space-y-1.5"><Label>Birim</Label>
              <Select value={f.unit} onValueChange={set("unit")}><SelectTrigger data-testid="br-unit"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Hedef ₺</Label><Input type="number" value={f.target_price} onChange={(e) => set("target_price")(e.target.value)} data-testid="br-price" /></div>
          </div>
          <div className="space-y-1.5"><Label>Şehir</Label>
            <Select value={f.city} onValueChange={set("city")}><SelectTrigger data-testid="br-city"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
              <SelectContent className="max-h-60">{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Açıklama</Label><Textarea value={f.description} onChange={(e) => set("description")(e.target.value)} rows={3} data-testid="br-desc" placeholder="İhtiyacınızı detaylıca anlatın" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading || !f.product} data-testid="br-submit" className="bg-emerald-600 hover:bg-emerald-700">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Oluştur
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
